import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import { Calendar } from "primereact/calendar";

const FundTransferCreate = () => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({
    date: new Date(),
  });
  const [error, setError] = useState({});
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromAccountBalance, setFromAccountBalance] = useState(null);
  const [toAccountBalance, setToAccountBalance] = useState(null);
  const [loadingFromBalance, setLoadingFromBalance] = useState(false);
  const [loadingToBalance, setLoadingToBalance] = useState(false);
  const [fromBalanceError, setFromBalanceError] = useState("");
  const [toBalanceError, setToBalanceError] = useState("");
  const [fromAmountError, setFromAmountError] = useState("");

  useEffect(() => {
    Http.get("/accounts/banks")
      .then((res) => {
        setBanks(res.data);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const fetchBankBalance = (bankId, accountType) => {
    if (!bankId) {
      if (accountType === "from") {
        setFromAccountBalance(null);
        setFromBalanceError("");
      } else {
        setToAccountBalance(null);
        setToBalanceError("");
      }
      return;
    }

    if (accountType === "from") {
      setLoadingFromBalance(true);
      setFromBalanceError("");
    } else {
      setLoadingToBalance(true);
      setToBalanceError("");
    }

    Http.get(`/accounts/get-single-bank-ledger-summary/${bankId}`)
      .then((res) => {
        if (res.data.success) {
          if (accountType === "from") {
            setFromAccountBalance(res.data.data);
          } else {
            setToAccountBalance(res.data.data);
          }
        } else {
          if (accountType === "from") {
            setFromBalanceError("Failed to fetch bank balance");
          } else {
            setToBalanceError("Failed to fetch bank balance");
          }
        }
      })
      .catch((error) => {
        console.error("Balance fetch error:", error);
        if (accountType === "from") {
          setFromBalanceError("Error fetching bank balance");
          setFromAccountBalance(null);
        } else {
          setToBalanceError("Error fetching bank balance");
          setToAccountBalance(null);
        }
      })
      .finally(() => {
        if (accountType === "from") {
          setLoadingFromBalance(false);
        } else {
          setLoadingToBalance(false);
        }
      });
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));

    // Fetch bank balance when bank is selected
    if (name === "from_account_id") {
      fetchBankBalance(value, "from");
    } else if (name === "to_account_id") {
      fetchBankBalance(value, "to");
    }

    // Validate amount vs from account balance
    if (name === "amount" || name === "from_account_id") {
      setTimeout(() => {
        const amount = parseFloat(name === "amount" ? value : inputs.amount) || 0;
        const hasFrom = name === "from_account_id" ? !!value : !!inputs.from_account_id;
        const rawBalance = fromAccountBalance?.total_balance;
        const balance = rawBalance !== undefined && rawBalance !== null
          ? parseFloat(String(rawBalance).replace(/[^0-9.-]/g, ""))
          : null;
        if (hasFrom && amount > 0 && balance !== null && !isNaN(balance) && amount > balance) {
          setFromAmountError("Transfer amount exceeds available balance in From Account!");
        } else {
          setFromAmountError("");
        }
      }, 0);
    }
  };

  const handleDateChange = (e) => {
    setInputs((values) => ({ ...values, date: e.value }));
  };

  const formSubmit = async () => {
    try {
      // Prevent submission if transfer exceeds from-account balance
      const amount = parseFloat(inputs.amount || 0) || 0;
      const rawBalance = fromAccountBalance?.total_balance;
      const balance = rawBalance !== undefined && rawBalance !== null
        ? parseFloat(String(rawBalance).replace(/[^0-9.-]/g, ""))
        : null;
      if (inputs.from_account_id && amount > 0 && balance !== null && !isNaN(balance) && amount > balance) {
        setFromAmountError("Transfer amount exceeds available balance in From Account!");
        setError({ general: ["Please correct the amount before submitting."] });
        return;
      } else {
        setFromAmountError("");
        setError({});
      }

      // Map frontend field names to match backend expectations
      const submitData = {
        from_account_id: inputs.from_account_id,
        to_account_id: inputs.to_account_id,
        date: inputs.date,
        amount: inputs.amount,
        description: inputs.description,
        reference: inputs.reference,
      };

      const res = await Http.post("/accounts/fund-transfers", submitData);
      navigate("/fund-transfers", {
        state: { message: res.data.message, type: "success" },
      });
    } catch (e) {
      setError(e.response?.data?.errors || { general: ["An error occurred"] });
    }
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Create Fund Transfer</h5>
        </div>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-body-title mg-sm-b-30">Transfer Information</h6>
            <div className="row">
              <div className="col-lg">
                <FormValidation error={error} />
              </div>
            </div>
            <div className="row">
              <div className="col-lg">
                <label>
                  Date <span className="text-danger">*</span>
                </label>
                <Calendar
                  value={inputs.date}
                  onChange={handleDateChange}
                  dateFormat="dd/mm/yy"
                  className="w-100"
                />
              </div>
              <div className="col-lg">
                <label>Reference</label>
                <input
                  type="text"
                  className="form-control"
                  name="reference"
                  value={inputs.reference || ""}
                  onChange={handleChange}
                  placeholder="Enter Reference"
                />
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-lg">
                <label>
                  From Account <span className="text-danger">*</span>
                </label>
                {/* <select
                  className="form-control"
                  name="from_account_id"
                  value={inputs.from_account_id || ""}
                  onChange={handleChange}
                >
                  <option value="">Select From Account</option>
                  {banks.map((bank) => (
                    <option key={bank.id} value={bank.id}>
                      {bank.name}
                    </option>
                  ))}
                </select> */}
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  name="from_account_id"
                  value={
                    banks
                      .map((bank) => ({
                        value: bank.id,
                        label: `${bank.name} - ${bank.account_number}`,
                      }))
                      .find(
                        (option) => option.value === inputs.from_account_id
                      ) || null
                  }
                  options={banks.map((bank) => ({
                    value: bank.id,
                    label: `${bank.name} - ${bank.account_number}`,
                  }))}
                  onChange={(selectedOption) => {
                    setInputs((values) => ({
                      ...values,
                      from_account_id: selectedOption.value,
                    }));
                    fetchBankBalance(selectedOption.value, "from");
                  }}
                />
              </div>
              <div className="col-lg">
                <label>
                  To Account <span className="text-danger">*</span>
                </label>
                {/* <select
                  className="form-control"
                  name="to_account_id"
                  value={inputs.to_account_id || ""}
                  onChange={handleChange}
                >
                  <option value="">Select To Account</option>
                  {banks.map((bank) => (
                    <option key={bank.id} value={bank.id}>
                      {bank.name}
                    </option>
                  ))}
                </select> */}
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  name="to_account_id"
                  value={
                    banks
                      .map((bank) => ({
                        value: bank.id,
                        label: `${bank.name} - ${bank.account_number}`,
                      }))
                      .find(
                        (option) => option.value === inputs.to_account_id
                      ) || null
                  }
                  options={banks.map((bank) => ({
                    value: bank.id,
                    label: `${bank.name} - ${bank.account_number}`,
                  }))}
                  onChange={(selectedOption) => {
                    setInputs((values) => ({
                      ...values,
                      to_account_id: selectedOption.value,
                    }));
                    fetchBankBalance(selectedOption.value, "to");
                  }}
                />
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-lg">
                {/* From Account Balance Display */}
                {inputs.from_account_id && (
                  <div
                    className="alert alert-warning"
                    style={{ marginBottom: "15px" }}
                  >
                    <strong>From Account Balance:</strong>
                    {loadingFromBalance ? (
                      <div className="d-flex align-items-center mt-2">
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        >
                          <span className="sr-only">Loading...</span>
                        </div>
                        <span>Loading balance...</span>
                      </div>
                    ) : fromBalanceError ? (
                      <div className="text-danger mt-2">
                        <i className="fa fa-exclamation-triangle me-1"></i>
                        {fromBalanceError}
                      </div>
                    ) : fromAccountBalance ? (
                      <div className="mt-2">
                        <div className="row">
                          <div className="col-md-4">
                            <small className="text-muted">Bank Name:</small>
                            <div className="font-weight-bold">
                              {fromAccountBalance.name}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <small className="text-muted">
                              Account Number:
                            </small>
                            <div className="font-weight-bold">
                              {fromAccountBalance.account_number}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <small className="text-muted">
                              Available Balance:
                            </small>
                            <div className="font-weight-bold text-warning">
                              ৳ {fromAccountBalance.total_balance}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="col-lg">
                {/* To Account Balance Display */}
                {inputs.to_account_id && (
                  <div
                    className="alert alert-success"
                    style={{ marginBottom: "15px" }}
                  >
                    <strong>To Account Balance:</strong>
                    {loadingToBalance ? (
                      <div className="d-flex align-items-center mt-2">
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        >
                          <span className="sr-only">Loading...</span>
                        </div>
                        <span>Loading balance...</span>
                      </div>
                    ) : toBalanceError ? (
                      <div className="text-danger mt-2">
                        <i className="fa fa-exclamation-triangle me-1"></i>
                        {toBalanceError}
                      </div>
                    ) : toAccountBalance ? (
                      <div className="mt-2">
                        <div className="row">
                          <div className="col-md-4">
                            <small className="text-muted">Bank Name:</small>
                            <div className="font-weight-bold">
                              {toAccountBalance.name}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <small className="text-muted">
                              Account Number:
                            </small>
                            <div className="font-weight-bold">
                              {toAccountBalance.account_number}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <small className="text-muted">
                              Current Balance:
                            </small>
                            <div className="font-weight-bold text-success">
                              ৳ {toAccountBalance.total_balance}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-lg">
                <label>
                  Amount <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="amount"
                  value={inputs.amount || ""}
                  onChange={handleChange}
                  placeholder="Enter Amount"
                />
                {fromAmountError && (
                  <div className="text-danger mt-1">
                    <i className="fa fa-exclamation-circle me-1"></i>
                    {fromAmountError}
                  </div>
                )}
              </div>
              <div className="col-lg">
                <label>Description</label>
                <textarea
                  className="form-control"
                  name="description"
                  value={inputs.description || ""}
                  onChange={handleChange}
                  placeholder="Enter Description"
                  rows="3"
                ></textarea>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-3">
                <button
                  className="btn btn-primary btn-block mg-b-10"
                  onClick={formSubmit}
                >
                  <i className="fa fa-save mg-r-10"></i> Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundTransferCreate;
